import React, { useState } from 'react';

import { useForm } from 'react-hook-form';
import moment from 'moment';

import classes from './NgzPerformanceMetricsTab.module.scss';
import { angularizeWithProviders } from '../../../../../shared/providers';
import { PageTitle } from '../../../../../shared/components/ui-components';
import { DateRangePicker } from '../../../../../shared/components/form-components';
import { getFormattedDate } from '../../../../../shared/utils/dateUtils';
import { dateFormat, oldDateFormat } from '../../../../../shared/constants/apiDateFormat';
import { MealStatisticWidget } from './MealStatisticWidget/MealStatisticWidget';
import { AvgPerformanceScoreWidget } from './AvgPerformanceScoreWidget/AvgPerformanceScoreWidget';
import { WaterConsumptionWidget } from './WaterConsumptionWidget/WaterConsumptionWidget';
import { PerformanceMetricsParams } from '../../../../interfaces/performance-metrics/PerformanceMetricsParams';
import { NutrientMetricsBlock } from './NutrientMetricsBlock/NutrientMetricsBlock';
import { useAutoSubmitForm } from '../../../../../shared/hooks/useAutoSubmitForm';
import { setRouteParams } from '../../../../../shared/utils/angularUtils';
import { getDateRangeFromParams } from '../../../../utils/getDateRangeFromParams/getDateRangeFromParams';
import { pathQueryParams } from '../../../../../shared/constants/pathQueryParams';

type NgzPerformanceMetricsTabProps = {
  customerId: number;
  registrationDate: string; // 'MM.DD.YYYY'
};

const NgzPerformanceMetricsTab = ({
  customerId,
  registrationDate,
}: NgzPerformanceMetricsTabProps) => {
  const customerRegistrationDate = getFormattedDate(
    registrationDate,
    dateFormat,
    oldDateFormat,
  );

  const dateRangeFromParams = getDateRangeFromParams(customerRegistrationDate);

  const defaultValues = {
    dateFrom: dateRangeFromParams.startDate,
    dateTo: dateRangeFromParams.endDate,
  };

  const [params, setParams] = useState<PerformanceMetricsParams>(defaultValues);
  const { control, watch, handleSubmit } = useForm<PerformanceMetricsParams>({
    defaultValues,
  });

  const onSubmit = handleSubmit((data) => {
    setParams(data);

    setRouteParams({
      [pathQueryParams.startDate]: data.dateFrom,
      [pathQueryParams.endDate]: data.dateTo,
    });
  });

  useAutoSubmitForm(onSubmit, watch);

  return (
    <>
      <PageTitle title="Performance Metrics">
        <DateRangePicker
          control={control}
          size="medium"
          variant="outlined"
          startPickerProps={{
            name: 'dateFrom',
            label: 'Start Date',
            minDate: moment(customerRegistrationDate),
            rules: { required: 'Start date is required' },
          }}
          endPickerProps={{
            name: 'dateTo',
            label: 'End Date',
            disableFuture: true,
            rules: { required: 'End date is required' },
          }}
          dataTestId="date-range-picker"
        />
      </PageTitle>
      <div className={classes.row}>
        <MealStatisticWidget customerId={customerId} params={params} />
        <AvgPerformanceScoreWidget customerId={customerId} params={params} />
      </div>
      <NutrientMetricsBlock customerId={customerId} params={params} />
      <WaterConsumptionWidget customerId={customerId} params={params} />
    </>
  );
};

angularizeWithProviders(NgzPerformanceMetricsTab, 'ngzPerformanceMetricsTab', {
  customerId: '<',
  registrationDate: '<',
});

export { NgzPerformanceMetricsTab };
